import type { CutoffState, CutoffWindowState } from './cutoffState';

export type PublishSessionStatus = 'pending' | 'confirmed' | 'disputed';

export interface HouseResultsSyncConfig {
  source?: string;
  autoFloridaSync?: boolean;
  autoNewYorkSync?: boolean;
}

export function remainingCutoffSeconds(state: Pick<CutoffState, 'hours' | 'mins' | 'secs'>): number {
  return state.hours * 3600 + state.mins * 60 + state.secs;
}

/** True when the house is set to auto-publish official draws (Florida / NY). */
export function isHouseAutoResultsEnabled(
  config: HouseResultsSyncConfig | null | undefined,
): boolean {
  const source = String(config?.source ?? 'florida');
  if (source === 'florida') return config?.autoFloridaSync !== false;
  if (source === 'newyork') return config?.autoNewYorkSync !== false;
  return false;
}

/**
 * Auto sync for day lands when night opens; for night when the next day opens.
 * Until then a missing session is "not yet", not a failure.
 */
export function isAutoPublishFailed(input: {
  windowState: CutoffWindowState;
  publishWindow: 'day' | 'night';
  sessionStatus?: PublishSessionStatus | null;
}): boolean {
  if (input.sessionStatus === 'confirmed') return false;
  if (input.sessionStatus === 'disputed') return true;

  if (input.publishWindow === 'day') {
    // Day auto sync lands when night opens (end of awaiting_results).
    return input.windowState === 'night_open' || input.windowState === 'next_day';
  }

  // Night auto sync lands at next day open.
  return input.windowState === 'day_open';
}

/** Midday/evening force-sync sessions the banker may recover right now. */
export type RecoverableLotterySession = 'midday' | 'evening';

/**
 * Only overdue missing auto-sync turns (outside their results window, still unpublished).
 * Not available while a turn is still waiting for the official feed on schedule.
 */
export function recoverableAutoSyncSessions(input: {
  config?: HouseResultsSyncConfig | null;
  windowState: CutoffWindowState;
  dayStatus?: PublishSessionStatus | null;
  nightStatus?: PublishSessionStatus | null;
}): RecoverableLotterySession[] {
  if (!isHouseAutoResultsEnabled(input.config)) return [];
  const sessions: RecoverableLotterySession[] = [];
  if (isAutoPublishFailed({
    windowState: input.windowState,
    publishWindow: 'day',
    sessionStatus: input.dayStatus,
  })) {
    sessions.push('midday');
  }
  if (isAutoPublishFailed({
    windowState: input.windowState,
    publishWindow: 'night',
    sessionStatus: input.nightStatus,
  })) {
    sessions.push('evening');
  }
  return sessions;
}

/**
 * Manual publish time gate: only during results-wait, or between cycles
 * (`next_day`) for the turn that just closed. Never while still accepting plays.
 */
export function isWithinPublishTimeGate(input: {
  windowState: CutoffWindowState;
  hours?: number;
  mins?: number;
  secs?: number;
}): boolean {
  const { windowState } = input;
  return windowState === 'awaiting_results' || windowState === 'next_day';
}

/**
 * Banker publish CTA:
 * - During results-wait / `next_day`: allow (unless already confirmed).
 * - After that: only when auto-sync failed for the recently closed turn.
 * Never while still accepting plays.
 */
export function isPublishResultsCtaActive(input: {
  windowState: CutoffWindowState;
  hours: number;
  mins: number;
  secs: number;
  config?: HouseResultsSyncConfig | null;
  publishWindow?: 'day' | 'night';
  sessionStatus?: PublishSessionStatus | null;
}): boolean {
  if (input.sessionStatus === 'confirmed') return false;

  if (isWithinPublishTimeGate(input)) return true;

  if (!isHouseAutoResultsEnabled(input.config)) return false;
  if (!input.publishWindow) return false;

  return isAutoPublishFailed({
    windowState: input.windowState,
    publishWindow: input.publishWindow,
    sessionStatus: input.sessionStatus,
  });
}
